import { http } from '@/shared/api/httpClient';
import type { AdminUserUpdateRequest, CreateUserRequest, UserProfile } from '../model/types';

/** Wywolania endpointow `/api/users/**`. */

/** Pobiera wszystkich uzytkownikow systemu (tylko ADMIN). */
export function getAllUsers(): Promise<UserProfile[]> {
  return http.get<UserProfile[]>('/users');
}

/** Tworzy nowe konto uzytkownika (`POST /api/users`). */
export function createUser(request: CreateUserRequest): Promise<UserProfile> {
  return http.post<UserProfile>('/users', request);
}

/** Aktualizuje imie, nazwisko, plec i status konta (tylko ADMIN). */
export function updateUser(id: string, request: AdminUserUpdateRequest): Promise<UserProfile> {
  return http.put<UserProfile>(`/users/${id}`, request);
}

/** Trwale usuwa konto uzytkownika (tylko ADMIN). */
export function deleteUser(id: string): Promise<void> {
  return http.delete<void>(`/users/${id}`);
}

/** Nadpisuje role globalne uzytkownika (tylko ADMIN; roli ADMIN nie mozna nadac). */
export function setUserRoles(id: string, roles: string[]): Promise<void> {
  return http.put<void>(`/users/${id}/roles`, roles);
}

/** Nadaje uzytkownikowi nowe haslo i uniewaznia jego sesje (tylko ADMIN). */
export function setUserPassword(id: string, password: string): Promise<void> {
  return http.put<void>(`/users/${id}/password`, { password });
}

/** Pobiera profil uzytkownika po identyfikatorze. */
export function getUserById(id: string): Promise<UserProfile> {
  return http.get<UserProfile>(`/users/${id}`);
}

/** Wyszukuje uzytkownikow po poczatku imienia lub nazwiska (maks. 20 wynikow). */
export function searchUsers(query: string): Promise<UserProfile[]> {
  return http.get<UserProfile[]>('/users/search', { query: { query } });
}

/**
 * Maksymalna porcja identyfikatorow w jednym zapytaniu batch.
 *
 * Identyfikatory jada w query stringu (80 UUID-ow to ok. 3 KB), a gateway
 * (Netty) odrzuca linie zadania dluzsze niz ok. 4 KB — turniej na 250 graczy
 * w jednym URL-u konczyl sie bledem i lista zostawala bez nazwisk.
 */
const BATCH_CHUNK_SIZE = 80;

/**
 * Pobiera wielu uzytkownikow naraz. Przydatne przy listach zawodnikow.
 * Dluzsze listy sa dzielone na rownolegle porcje i sklejane w jedna odpowiedz.
 */
export async function getUsersByIds(ids: readonly string[]): Promise<UserProfile[]> {
  if (ids.length === 0) return [];

  const chunks: string[][] = [];
  for (let start = 0; start < ids.length; start += BATCH_CHUNK_SIZE) {
    chunks.push([...ids.slice(start, start + BATCH_CHUNK_SIZE)]);
  }

  const responses = await Promise.all(
    chunks.map((chunk) =>
      http.get<UserProfile[]>('/users/batch', { query: { ids: chunk.join(',') } }),
    ),
  );
  return responses.flat();
}
